import React, { useEffect, useRef } from 'react';
import IForm, { ConsentField, Field, Options } from 'core/render/questions/Form';
import { BasicQuestionComponent } from '../interfaces/QuestionComponent';
import useI18n from 'i18n/useI18n';
import { Input, PhoneInput, utils } from '@etiquette-ui/inputs-dropdowns';
import { RadioButton, Checkbox } from '@etiquette-ui/selectors';
import styled from 'styled-components';
import AnimateTransition from 'components/Animate';
import { cleanId } from 'core/utils';
import SectionTitle from 'components/SectionTitle';
import { useScroll } from 'providers/scroll';
import { PRIVACY_POLICY_URL } from 'resources/constants/urls';
import { PRIMARY_DARK } from '@etiquette-ui/colors';
import useSurvey from 'state/survey';

const FieldContainer = styled.div`
    margin: 15px 0;

    svg {
        filter: brightness(0);
    }
`;

const CustomCheckbox = styled(Checkbox)`
    width: 80%;
    margin-left: 15px;
    > input {
        flex-shrink: 0;
    }
`

const FieldComponent: React.FC<{ field: Field | Options; dataAccess: [any, (d: any) => void] }> = ({ field, dataAccess }) => {
    const { translateObject, translate } = useI18n();
    const [state, setState] = dataAccess;
    const phoneRef = useRef<HTMLInputElement>(null);

    const onChangePhoneInput = (e: any) => {
        const value = e.target.value;

        setState({ ...state, phone: value });
        if (!phoneRef.current) return;

        if (!value && !!field.isRequired) {
            phoneRef.current.setCustomValidity(translate('Invalid Phone Number'));
            phoneRef.current.reportValidity();
            return;
        }

        if (!value && !field.isRequired) {
            phoneRef.current.setCustomValidity('');
            return;
        }

        const countryCode = utils.findCountryPhoneCode(state.countryCode);
        if (!countryCode) {
            phoneRef.current.setCustomValidity('');
            return;
        }
        if (utils.isValidFullNumber(`+${countryCode}${value}`)) {
            phoneRef.current.setCustomValidity('');
        } else {
            phoneRef.current.setCustomValidity(translate('Invalid Phone Number'));
            phoneRef.current.reportValidity();
        }
    };

    switch (field.type) {
        case 'email':
            return (
                <FieldContainer>
                    <Input
                        iconProps={{
                            icon: field.icon,
                        }}
                        inputProps={{
                            onChange: (e: any) => {
                                setState({ ...state, email: e.target.value });
                            },
                            value: state.email,
                            required: !!field.isRequired,
                            pattern: '[A-Za-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,12}$',
                        }}
                        labelProps={{
                            label: translateObject(field.placeholder),
                        }}
                    />
                    {!!(field as ConsentField).consentMessage && (<CustomCheckbox
                        style={{ marginTop: '10px' }}
                        checked={state.consent}
                        onClick={() => {
                            setState({ ...state, consent: !state.consent });
                        }}
                    >
                        <p style={{ fontSize: '0.8em', margin: 0, flexShrink: 1 }}>
                            {translateObject((field as ConsentField).consentMessage!)}
                        </p>
                    </CustomCheckbox>)}
                </FieldContainer>
            );
        case 'name':
        case 'open':
            return (
                <FieldContainer>
                    <Input
                        iconProps={{
                            icon: field.icon,
                        }}
                        inputProps={{
                            onChange: (e: any) => {
                                setState(e.target.value);
                            },
                            value: state,
                            required: !!field.isRequired,
                        }}
                        labelProps={{
                            label: translateObject(field.placeholder),
                        }}
                    />
                </FieldContainer>
            );
        case 'phone':
            return (
                <FieldContainer>
                    <PhoneInput
                        ref={phoneRef}
                        countrySelectProps={{
                            countriesLabel: translate('All Countries'),
                            country: state.countryCode,
                            onChange: (country: string) => {
                                setState({ ...state, countryCode: country });
                            },
                            preferredCountriesCodes: ['MX', 'US', 'CA'],
                        }}
                        iconProps={{
                            icon: field.icon,
                        }}
                        inputProps={{
                            onChange: onChangePhoneInput,
                            value: state.phone,
                            required: !!field.isRequired,
                        }}
                        labelProps={{
                            label: translateObject(field.placeholder),
                        }}
                    />
                    {!!(field as ConsentField).consentMessage && (<CustomCheckbox
                        style={{ marginTop: '10px' }}
                        checked={state.consent}
                        onClick={() => {
                            setState({ ...state, consent: !state.consent });
                        }}
                    >
                        <p style={{ fontSize: '0.8em', margin: 0, flexShrink: 1 }}>
                            {translateObject((field as ConsentField).consentMessage!)}
                        </p>
                    </CustomCheckbox>)}
                </FieldContainer>
            );
        case 'radio':
            return (
                <FieldContainer style={{ display: 'flex', gap: '15px', padding: '5px 0' }}>
                    {field.options.map((opt) => (
                        <RadioButton
                            key={opt.id}
                            checked={state === opt.id}
                            onClick={() => setState(opt.id)}
                            inputProps={{ name: !!field.isRequired ? field.id : undefined, required: !!field.isRequired }}
                        >
                            {translateObject(opt.translations)}
                        </RadioButton>
                    ))}
                </FieldContainer>
            );
        case 'checkbox':
            return (
                <FieldContainer style={{ display: 'flex', gap: '15px', padding: '5px 0' }}>
                    {field.options.map((opt) => (
                        <Checkbox
                            key={opt.id}
                            checked={state.includes(opt.id)}
                            onClick={() => {
                                if (state.includes(opt.id)) {
                                    setState(state.filter((item: string) => item !== opt.id));
                                    return;
                                }
                                setState([...state, opt.id]);
                            }}
                            inputProps={{ name: !!field.isRequired ? field.id : undefined, required: !!field.isRequired }}
                        >
                            {translateObject(opt.translations)}
                        </Checkbox>
                    ))}
                </FieldContainer>
            );
        default:
            return <div></div>;
    }
};

const Form: React.FC<BasicQuestionComponent<IForm>> = ({ id, question, dataAccess }) => {
    const { translateObject, translate } = useI18n();
    const { registerQuestion } = useScroll();
    const { state } = useSurvey();

    useEffect(() => {
        registerQuestion(id, true);
        return () => {
            registerQuestion(id, false);
        };
    }, []);

    const needsPolicyAndTerms = question.blocks.some((block) =>
        block.fields.some((field) => field.type === 'email' || field.type === 'phone')
    );

    const meta = state.survey?.meta;

    return (
        <AnimateTransition id={cleanId(id)}>
            {question.blocks.map((block, i) => (
                <div key={i}>
                    {block.title !== null && (
                        <SectionTitle style={{ textAlign: 'center' }}>{translateObject(block.title)}</SectionTitle>
                    )}
                    {block.fields.map((field) => (
                        <FieldComponent key={field.id} field={field} dataAccess={dataAccess(field.id)} />
                    ))}
                </div>
            ))}
            {needsPolicyAndTerms && (
                <div>
                {meta?.privacyPolicyURL && meta?.groupLegalName && (
                    <FieldContainer>
                        <Checkbox
                            checked={false}
                            onClick={() => {}}
                        >
                            {translate("READ_PRIVACY")}
                            <a style={{ color: PRIMARY_DARK, margin: '0 4px' }} href={meta.privacyPolicyURL} target='_blank' rel='noopener noreferrer'>
                                {translate('PRIVACY_POLICY')}
                            </a>
                            {translate('from')}{` ${meta?.groupLegalName}`}
                        </Checkbox>
                    </FieldContainer>
                )}
                {meta?.termsAndContitionsURL && meta?.groupLegalName && (
                    <FieldContainer>
                        <Checkbox
                            checked={false}
                            onClick={() => {}}
                        >
                            {translate("READ_TERMS")}
                            <a style={{ color: PRIMARY_DARK, margin: '0 4px' }} href={meta.termsAndContitionsURL} target='_blank' rel='noopener noreferrer'>
                                {translate('TERMS_CONDITIONS')}
                            </a>
                            {translate('from')}{` ${meta?.groupLegalName}`}
                        </Checkbox>
                    </FieldContainer>
                )}
                </div>
            )}
        </AnimateTransition>
    );
};

export default Form;
